import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  reportTemplatesService,
  CreateReportTemplateInput,
  UpdateReportTemplateInput,
} from "../../services/admin/reporttemplates.service";

export function useReportTemplatesAdmin() {
  return useQuery({
    queryKey: ["admin", "report-templates"],
    queryFn: reportTemplatesService.getAll,
  });
}

export function useActiveReportTemplates(enabled = true) {
  return useQuery({
    queryKey: ["admin", "report-templates", "active"],
    queryFn: reportTemplatesService.getActive,
    enabled,
  });
}

export function useReportTemplate(id?: string) {
  return useQuery({
    queryKey: ["admin", "report-templates", id],
    queryFn: () => reportTemplatesService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateReportTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateReportTemplateInput) =>
      reportTemplatesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "report-templates"],
      });
      notifications.show({
        title: "Success",
        message: "Report template created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create report template",
        color: "red",
      });
    },
  });
}

export function useUpdateReportTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateReportTemplateInput) =>
      reportTemplatesService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "report-templates"],
      });
      notifications.show({
        title: "Success",
        message: "Report template updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update report template",
        color: "red",
      });
    },
  });
}

export function useDeleteReportTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reportTemplatesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "report-templates"],
      });
      notifications.show({
        title: "Success",
        message: "Report template deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete report template",
        color: "red",
      });
    },
  });
}

export function useSetDefaultReportTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reportTemplatesService.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "report-templates"],
      });
      notifications.show({
        title: "Success",
        message: "Default template updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to set default template",
        color: "red",
      });
    },
  });
}
